"use client";

import {
  buildUsrcCpsReferralAuthInstruction,
  resolveUsrcCpsAuthPortalPath,
  VA_RFS_FORM_URL,
} from "@/configuration/groups/usrc-cps/referral-auth-instructions";
import { ReferralAuthInstructionsCard } from "@/src/components/tasks/referral-auth-instructions-card";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { useReferralAuthOpenAuthorization } from "@/src/hooks/use-referral-auth-open-authorization";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { resolveReferralAuthOnFile } from "@/src/lib/prior-authorization/referral-auth-on-file";
import type { PriorAuthorization } from "@/src/models/documents/prior-authorization/prior-authorization";
import { Referral } from "@/src/models/documents/referral";

interface UsrcCpsReferralAuthInstructionsProps {
  referral: Referral;
  linkedPriorAuthorizations: PriorAuthorization[];
  taskId: string;
  taskPriorAuthorization?: PriorAuthorization | null;
  startTrackingCaseDisabled?: boolean;
  subdomain: string;
}

export function UsrcCpsReferralAuthInstructions({
  referral,
  linkedPriorAuthorizations,
  taskId,
  taskPriorAuthorization,
  startTrackingCaseDisabled = false,
  subdomain,
}: UsrcCpsReferralAuthInstructionsProps) {
  const { eligibilityCheck } = useStediEligibilityCheck(referral.id);
  const authOnFile = resolveReferralAuthOnFile(referral, linkedPriorAuthorizations, taskPriorAuthorization);
  const path = resolveUsrcCpsAuthPortalPath({
    subdomain,
    primaryCoverage: referral.primaryCoverage,
    secondaryCoverage: referral.secondaryCoverage,
  });
  const instruction = buildUsrcCpsReferralAuthInstruction({ path, authOnFile });
  const { handleOpenAuthorization, isOpeningAuthorization } = useReferralAuthOpenAuthorization({
    taskId,
    taskPriorAuthorization,
    linkedPriorAuthorizations,
  });

  const instructionNode = instruction.showVaFormLink ? (
    <>
      {instruction.text}{" "}
      <a href={VA_RFS_FORM_URL} target="_blank" rel="noopener noreferrer" className="underline">
        VA Form 10-10172
      </a>
      .
    </>
  ) : (
    instruction.text
  );

  return (
    <ReferralAuthInstructionsCard
      instruction={instructionNode}
      openButtonTooltip={
        startTrackingCaseDisabled
          ? "Finish updating the task before opening authorization tracking."
          : "Opens the authorization in the Authorizations tab to track status."
      }
      isOpenButtonDisabled={startTrackingCaseDisabled || isOpeningAuthorization}
      isOpening={isOpeningAuthorization}
      onOpenAuthorization={handleOpenAuthorization}
      footer={eligibilityCheck ? <StediEligibilitySummary check={eligibilityCheck} /> : null}
    />
  );
}
